import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, Modal, Alert, Platform, Pressable } from 'react-native';

import { supabase } from '../../shared/services/supabaseService';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { uiText } from '../../shared/utils/uiText';

interface JoinFriendsByInviteCodeModalProps {
  visible: boolean;
  onClose: () => void;
  onJoined: () => void;
}

const JoinFriendsByInviteCodeModal: React.FC<JoinFriendsByInviteCodeModalProps> = ({ visible, onClose, onJoined }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!inviteCode) {
      Alert.alert(uiText('familiesFriends.joinCodeRequired'));
      return;
    }
    setJoining(true);
    let code = inviteCode.trim().toUpperCase();
    // extract short code if link
    const linkMatch = code.match(/([A-Z2-9]{4,6})$/);
    if (linkMatch) code = linkMatch[1];
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('Not logged in');
      // try join by code first
      const { error } = await supabase.rpc('join_friends_group_by_code', { _group_code: code, _user_id: userId });
      if (!error) {
        Alert.alert(uiText('successTitle'), uiText('familiesFriends.joinFriendsSuccess'));
        setInviteCode('');
        onJoined();
        onClose();
        setJoining(false);
        return;
      }
      
      Alert.alert(uiText('successTitle'), uiText('familiesFriends.joinFriendsSuccess'));
      setInviteCode('');
      onJoined();
      onClose();
    } catch (e: any) {
      Alert.alert(uiText('errorTitle'), e.message || uiText('joinFriendsFailed'));
    } finally {
      setJoining(false);
    }
  };

  const Content = (
    <View style={styles.modal}>
      <Text style={styles.title}>{uiText('joinFriendsTitle')}</Text>
      <TextInput
        style={styles.input}
        placeholder={uiText('inviteCodePlaceholder')}
        value={inviteCode}
        onChangeText={setInviteCode}
        autoCapitalize="none"
      />
      <PrimaryButton title={joining ? 'Joining...' : 'Join Group'} onPress={handleJoin} disabled={joining || !inviteCode} />
      <PrimaryButton title={uiText('cancel')} onPress={onClose} style={styles.cancelButton} />
    </View>
  );

  if (Platform.OS === 'web') {
    return visible ? (
      <Pressable onPress={onClose}>
        <View style={styles.overlayWeb}>
          <Pressable onPress={(e: any) => e.stopPropagation()}>{Content}</Pressable>
        </View>
      </Pressable>
    ) : null;
  }

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlayNative}>{Content}</View>
    </Modal>
  );
};

const baseModal: any = {
  backgroundColor: '#fff',
  padding: 24,
  borderRadius: 16,
  width: '90%',
  maxWidth: 400,
};

const styles = StyleSheet.create({
  overlayWeb: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlayNative: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: baseModal,
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 12, textAlign: 'center' },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 8, padding: 10, marginVertical: 8 },
  cancelButton: { marginTop: 8, backgroundColor: '#eee' },
});

export default JoinFriendsByInviteCodeModal;
